// The canonical mapping from an opportunity to the (service_type,
// service_family, transaction_type) it becomes when won.
//
// Extracted from workers/api/src/routes/opportunity-transitions.ts (2026-07-20)
// so BOTH the won-transition route (which creates the service record +
// transaction) AND the Automations row-source (which needs service_type /
// service_family in an opportunity's evaluation context so rules can qualify
// "…and the product is Medishare") derive it identically — no drift between
// what a won opportunity becomes and what an automation condition matches.
//
// ─── Why this is keyed on (company, type) as of 2026-07-30 ──────────
//
// Joey Ellis, screen recording 2026-07-30: a won Mortgage Pre-Qual landed
// on the Processing board as a `policy_issuance` — an INSURANCE record —
// so it vanished the moment he filtered the board to Mortgage Loan
// Progression ("is there a way to fix that?").
//
// Root cause: this map held 10 opportunity types while the stage-template
// registry (shared/db/company-stage-templates.ts) recognizes 20+, and the
// fallback for anything unlisted was a hardcoded `policy_issuance` /
// `policy` — regardless of company. Every Mortgage / Financial / Planning /
// Realty opportunity carrying an AC-migrated type slug (`mortgage_lead`,
// `mortgage_prequalification`, `financial_lead`, `realty_shopping`, …)
// silently became an insurance-flavored record on a board its company
// doesn't own. Joey was the first to notice; the bug was never
// mortgage-specific.
//
// Two structural fixes, both required:
//
//   1. The map is keyed `${companySlug}:${opportunityType}` and covers
//      EVERY opportunity_type the stage-template registry knows. This also
//      resolves a real collision: `insurance_quote` exists under BOTH
//      Insurance (a P&C quote → policy) and Planning (Ryan's health quoting
//      funnel → case). A type-only map cannot tell those apart.
//
//   2. The fallback is COMPANY-AWARE. An unknown type inside a known
//      company resolves to that company's own default product + Processing
//      board — never across a company line. Only a genuinely unknown
//      company falls through to the global default, and that path is
//      reported by `isDerivedByFallback()` so callers can flag it rather
//      than let a wrong guess look authoritative.
//
// Adding a new opportunity_type? Add it to BOTH this map and the TEMPLATES
// table in shared/db/company-stage-templates.ts. The parity test
// (tests/unit/opportunity-service-map.test.ts) fails the build if a type
// exists in the registry but not here.

export interface OpportunityServiceDefaults {
  service_type: string;
  service_family: string;
  transaction_type: string;
}

// ─── Per-company defaults ─────────────────────────────────────────
//
// The product + Processing board a won opportunity becomes when its
// specific type carries no entry below. Each transaction_type here is a
// real key in the stage-template TEMPLATES table, so the resulting
// Processing record always lands on a board that exists for its company.
//
// DECISION: mortgage's company default is Traditional
// (`mortgage_loan_progression`), not Private Money. Private Money is the
// narrower, explicitly-typed product line (`private_money_loan`), and a
// mis-defaulted PM record on Joey's Traditional board is far more
// recoverable than a Traditional file landing on Mike's one-column PM
// board.
export const COMPANY_SERVICE_DEFAULTS: Record<string, OpportunityServiceDefaults> = {
  insurance: {
    service_type: "insurance_policy",
    service_family: "policy",
    transaction_type: "policy_issuance",
  },
  mortgage: {
    service_type: "purchase_mortgage",
    service_family: "loan",
    transaction_type: "mortgage_loan_progression",
  },
  financial: {
    service_type: "managed_portfolio",
    service_family: "portfolio",
    transaction_type: "financial_client_onboarding",
  },
  // DECISION (2026-08-08, Ryan's recording): planning's company default is
  // the Life & Health delivery board, NOT the estate-plan drafting board.
  // An unrecognized Planning opportunity type is far likelier to be one of
  // Ryan's insurance-style funnels than a will — those are the funnels that
  // carry AC-migrated slugs — and defaulting to estate plan is precisely
  // what filed two won life cases as estate plans.
  planning: {
    service_type: "planning_case",
    service_family: "case",
    transaction_type: "life_health_case",
  },
  realty: {
    service_type: "home_purchase",
    service_family: "home_purchase",
    transaction_type: "realty_transaction",
  },
};

// The last-resort default when even the COMPANY is unknown (a company slug
// that isn't one of the five client-facing companies). Callers should treat
// this as "could not derive" — see isDerivedByFallback().
const GLOBAL_FALLBACK: OpportunityServiceDefaults = {
  service_type: "service",
  service_family: "policy",
  transaction_type: "policy_issuance",
};

// ─── Per (company, opportunity_type) map ──────────────────────────
//
// Keys mirror the opportunity_type slugs in company-stage-templates.ts's
// TEMPLATES table. Ambiguous types (a quote that hasn't yet named its
// product line) resolve to the company's generic product rather than
// guessing a specific one — a wrong-but-specific `home_policy` on a record
// that's really auto is a number the team would read as authoritative
// ("a number the team sees is a promise"). The advisor can retype the
// service record; what matters here is that the Processing record lands on
// the right board.
export const OPPORTUNITY_SERVICE_DEFAULTS: Record<
  string,
  OpportunityServiceDefaults
> = {
  // ── Insurance ──
  // `insurance_quote` is AC's generic quote type (1,828 migrated rows) and
  // does not say which line of business was bound — hence the generic
  // `insurance_policy` product.
  "insurance:insurance_quote": {
    service_type: "insurance_policy",
    service_family: "policy",
    transaction_type: "policy_issuance",
  },
  "insurance:home_policy_quote": {
    service_type: "home_policy",
    service_family: "policy",
    transaction_type: "policy_issuance",
  },
  "insurance:auto_policy_quote": {
    service_type: "auto_policy",
    service_family: "policy",
    transaction_type: "policy_issuance",
  },
  "insurance:umbrella_policy_quote": {
    service_type: "umbrella_policy",
    service_family: "policy",
    transaction_type: "policy_issuance",
  },
  // Brandon's quote types (2026-09-28, folded 2026-09-29). A landlord
  // quote binds a dwelling-fire policy, the product the OS already calls
  // "Dwelling / Landlord". ATVs and motorcycles share one policy line
  // ("ATV / Motorcycle"), and travel trailers ride on the RV line.
  "insurance:landlord_policy_quote": {
    service_type: "dwelling_fire_policy",
    service_family: "policy",
    transaction_type: "policy_issuance",
  },
  "insurance:secondary_home_policy_quote": {
    service_type: "secondary_home_policy",
    service_family: "policy",
    transaction_type: "policy_issuance",
  },
  "insurance:motorcycle_policy_quote": {
    service_type: "motorcycle_policy",
    service_family: "policy",
    transaction_type: "policy_issuance",
  },
  "insurance:rv_policy_quote": {
    service_type: "rv_policy",
    service_family: "policy",
    transaction_type: "policy_issuance",
  },
  // RETIRED 2026-09-29, never offered in production: separate travel
  // trailer and ATV quotes existed for one night on staging, where the
  // synthetic seed created ~500 of them. Kept only so a win on one of
  // those still lands on the line it belongs to. Not in any picker.
  "insurance:travel_trailer_policy_quote": {
    service_type: "rv_policy",
    service_family: "policy",
    transaction_type: "policy_issuance",
  },
  "insurance:atv_policy_quote": {
    service_type: "motorcycle_policy",
    service_family: "policy",
    transaction_type: "policy_issuance",
  },

  // ── Mortgage ──
  // Traditional origination — every non-PM type shares Joey's one board.
  // `pre_qualification` (the New Opportunity modal's slug) and
  // `mortgage_prequalification` (the AC-migrated slug) are the same product.
  "mortgage:pre_qualification": {
    service_type: "purchase_mortgage",
    service_family: "loan",
    transaction_type: "mortgage_loan_progression",
  },
  "mortgage:mortgage_prequalification": {
    service_type: "purchase_mortgage",
    service_family: "loan",
    transaction_type: "mortgage_loan_progression",
  },
  "mortgage:mortgage_lead": {
    service_type: "purchase_mortgage",
    service_family: "loan",
    transaction_type: "mortgage_loan_progression",
  },
  "mortgage:purchase_mortgage": {
    service_type: "purchase_mortgage",
    service_family: "loan",
    transaction_type: "mortgage_loan_progression",
  },
  "mortgage:refinance_mortgage": {
    service_type: "refinance_mortgage",
    service_family: "loan",
    transaction_type: "mortgage_loan_progression",
  },
  // Private Money — Mike Zins's separate product line and separate board.
  "mortgage:private_money_loan": {
    service_type: "private_money_loan",
    service_family: "loan",
    transaction_type: "private_money_loan_progression",
  },

  // ── Financial ──
  "financial:financial_needs_assessment": {
    service_type: "managed_portfolio",
    service_family: "portfolio",
    transaction_type: "financial_client_onboarding",
  },
  "financial:financial_lead": {
    service_type: "managed_portfolio",
    service_family: "portfolio",
    transaction_type: "financial_client_onboarding",
  },
  // The fee-based planning membership (the $189/mo tier), not an AUM
  // portfolio — it delivers a Financial Plan.
  "financial:financial_membership": {
    service_type: "financial_plan",
    service_family: "portfolio",
    transaction_type: "financial_client_onboarding",
  },

  // ── Planning ──
  // Ryan Delviken's funnels. NOTE the `insurance_quote` collision: under
  // Planning this is the HEALTH quoting funnel, not a P&C policy.
  //
  // Every product below lands on one of THREE Planning delivery boards:
  // health/Medicare-shaped products stay on Life & Health Case (its name
  // is now a historical leftover — see the board-label comment in
  // company-stage-templates.ts), medically-underwritten life-shaped
  // products (life, disability, annuity) split onto their own board
  // (2026-08-29, Grant: "let Ryan do what he wants. Split life and
  // health." — Ryan: the shared board "looks like a life insurance
  // case" and he wanted it more health-specific; the existing checklist
  // content already treated these as a different underwriting shape —
  // see PLANNING_LIFE_HEALTH in condition-templates.ts, unchanged by
  // this split, still the right checklist for both boards' service
  // types), and the estate plan keeps its own signing-ceremony board.
  // Before the Life & Health board existed (pre-2026-08-08) these all
  // pointed at `estate_plan_case`, which is why Ryan's two won life
  // cases once showed up as estate plans.
  "planning:insurance_quote": {
    service_type: "health_case",
    service_family: "case",
    transaction_type: "life_health_case",
  },
  "planning:health_lead": {
    service_type: "health_case",
    service_family: "case",
    transaction_type: "life_health_case",
  },
  "planning:life_di_lead": {
    service_type: "life_case",
    service_family: "case",
    transaction_type: "planning_life_case",
  },
  // Long-term care (LTC health intake, 2026-09-23). A medically
  // underwritten product like life and disability, so it shares their
  // funnel and their delivery board; its own type so the case says what
  // it is (Brian had nowhere to track "in underwriting with Mutual of
  // Omaha"). Distinct from Insurance's `long_term_care_policy`, which is
  // a P&C-side policy kind, not a Planning case.
  "planning:long_term_care": {
    service_type: "long_term_care_case",
    service_family: "case",
    transaction_type: "planning_life_case",
  },
  // Employer groups (2026-10-29). Winning a group sale opens the
  // implementation file on the employer's board. It does NOT become an
  // individual case: the group plan record is its own thing (design step 3),
  // and the employees' certificates are their own records on their own
  // households. service_type/family only name the board's company default so
  // the map stays total; nothing materializes from them (see
  // product-materialization.ts, which refuses employer files).
  "planning:group_ltc": {
    service_type: "planning_case",
    service_family: "case",
    transaction_type: "group_ltc_implementation",
  },
  "planning:annuity": {
    service_type: "annuity_case",
    service_family: "case",
    transaction_type: "planning_life_case",
  },
  "planning:estate_plan": {
    service_type: "estate_plan",
    service_family: "case",
    transaction_type: "estate_plan_case",
  },
  "planning:estate_plan_consultation": {
    service_type: "estate_plan",
    service_family: "case",
    transaction_type: "estate_plan_case",
  },

  // ── Realty ──
  // Sell side.
  "realty:listing_consultation": {
    service_type: "listing",
    service_family: "listing",
    transaction_type: "realty_transaction",
  },
  // Rentals (2026-09-22, shared/realty/rental.ts). Their own board,
  // because a lease has no escrow, appraisal or repair negotiation.
  "realty:landlord_consultation": {
    service_type: "rental_listing",
    service_family: "listing",
    transaction_type: "realty_rental",
  },
  "realty:tenant_consultation": {
    service_type: "home_rental",
    service_family: "home_purchase",
    transaction_type: "realty_rental",
  },
  // Buy side. `realty_shopping` / `realty_purchase` are AC-migrated slugs;
  // `realty_lead` is ambiguous and follows the company default (buy side,
  // the majority of Realty leads).
  "realty:buyer_consultation": {
    service_type: "home_purchase",
    service_family: "home_purchase",
    transaction_type: "realty_transaction",
  },
  "realty:realty_purchase": {
    service_type: "home_purchase",
    service_family: "home_purchase",
    transaction_type: "realty_transaction",
  },
  "realty:realty_shopping": {
    service_type: "home_purchase",
    service_family: "home_purchase",
    transaction_type: "realty_transaction",
  },
  "realty:realty_lead": {
    service_type: "home_purchase",
    service_family: "home_purchase",
    transaction_type: "realty_transaction",
  },
};

/**
 * Derive (service_type, service_family, transaction_type) for a won
 * opportunity.
 *
 * Resolution order (most specific → least):
 *   1. `${companySlug}:${opportunityType}` — the exact product.
 *   2. The company's default product + Processing board.
 *   3. The global fallback (unknown company only).
 *
 * `companySlug` is optional ONLY for backwards compatibility with callers
 * that genuinely lack it; pass it whenever it is available, because without
 * it steps 1 and 2 are both unreachable and every opportunity resolves to
 * the global fallback — the exact bug this map was rewritten to fix.
 */
export function deriveOpportunityServiceDefaults(
  opportunityType: string,
  companySlug?: string | null,
): OpportunityServiceDefaults {
  const company = (companySlug ?? "").trim();
  if (company) {
    const exact = OPPORTUNITY_SERVICE_DEFAULTS[`${company}:${opportunityType}`];
    if (exact) return exact;
    const byCompany = COMPANY_SERVICE_DEFAULTS[company];
    if (byCompany) return byCompany;
  }
  return GLOBAL_FALLBACK;
}

/**
 * True when derivation could not find a company-specific answer and fell
 * through to the global default — i.e. the returned product/board is a
 * guess, not a mapping.
 *
 * The won-transition route uses this to REFUSE the conversion rather than
 * quietly create an insurance record for a company it couldn't identify.
 * (An unknown type inside a KNOWN company is not a fallback in this sense:
 * it still lands on that company's own board, which is correct-by-company
 * even when the specific product is generic.)
 */
export function isDerivedByFallback(companySlug?: string | null): boolean {
  const company = (companySlug ?? "").trim();
  return !company || !COMPANY_SERVICE_DEFAULTS[company];
}

// ─── Direct-create shape (no precursor Opportunity) ────────────────
//
// A Processing/transaction record can now be created directly (Nate
// 2026-08-04: "It would appear as of right now I have no way of adding a
// new transaction" — for a house already under contract that never
// passed through Opportunities). That path needs the SAME
// (service_family, transaction_type) a won opportunity would produce,
// but keyed off a chosen service_type instead of an opportunity_type —
// the create form asks "what is this?" (Purchase Mortgage, Managed
// Portfolio, Listing…), not "what funnel did it come from?".
//
// Kept as an explicit small table rather than reverse-derived from
// OPPORTUNITY_SERVICE_DEFAULTS above: that map's keys are opportunity
// types (a different vocabulary — e.g. "pre_qualification") and its
// values aren't 1:1 invertible (several opportunity types collapse to
// one service_type). This table is deliberately the minimal, direct
// answer to "given this company and this product, which Processing
// board does it belong on" — every entry mirrors an existing board key
// in shared/db/company-stage-templates.ts (checked by the parity test).
export interface ServiceShapeForType {
  service_family: string;
  transaction_type: string;
}

const SERVICE_SHAPE_BY_TYPE: Record<string, ServiceShapeForType> = {
  // Insurance — one board for every policy kind.
  ...Object.fromEntries(
    [
      "home_policy",
      "auto_policy",
      "umbrella_policy",
      "life_policy",
      "health_policy",
      "medicare_policy",
      "disability_policy",
      "long_term_care_policy",
      "renters_policy",
      "condo_policy",
      "dwelling_fire_policy",
      "watercraft_policy",
      "rv_policy",
      "motorcycle_policy",
      "secondary_home_policy",
      "commercial_policy",
      "flood_policy",
      "golf_cart_policy",
      "other_policy",
      "insurance_policy",
    ].map((t) => [
      `insurance:${t}`,
      { service_family: "policy", transaction_type: "policy_issuance" },
    ]),
  ),
  // Mortgage — Traditional vs Private Money are separate boards.
  "mortgage:purchase_mortgage": { service_family: "loan", transaction_type: "mortgage_loan_progression" },
  "mortgage:refinance_mortgage": { service_family: "loan", transaction_type: "mortgage_loan_progression" },
  "mortgage:heloc": { service_family: "loan", transaction_type: "mortgage_loan_progression" },
  "mortgage:heloan": { service_family: "loan", transaction_type: "mortgage_loan_progression" },
  "mortgage:reverse_mortgage": { service_family: "loan", transaction_type: "mortgage_loan_progression" },
  "mortgage:private_money_loan": { service_family: "loan", transaction_type: "private_money_loan_progression" },
  // Financial — one board.
  "financial:managed_portfolio": { service_family: "portfolio", transaction_type: "financial_client_onboarding" },
  "financial:financial_plan": { service_family: "portfolio", transaction_type: "financial_client_onboarding" },
  "financial:retirement_plan": { service_family: "portfolio", transaction_type: "financial_client_onboarding" },
  "financial:advisory_relationship": { service_family: "portfolio", transaction_type: "financial_client_onboarding" },
  // Account registrations (2026-09-10). Same board and same family as
  // every other Financial product — the registration is what kind of
  // account it is, not a different shape of work. Matches the 468 rows
  // already in production, every one of which carries
  // service_family='portfolio'.
  "financial:ira_traditional": { service_family: "portfolio", transaction_type: "financial_client_onboarding" },
  "financial:ira_roth": { service_family: "portfolio", transaction_type: "financial_client_onboarding" },
  "financial:joint": { service_family: "portfolio", transaction_type: "financial_client_onboarding" },
  "financial:trust": { service_family: "portfolio", transaction_type: "financial_client_onboarding" },
  "financial:sep_ira": { service_family: "portfolio", transaction_type: "financial_client_onboarding" },
  "financial:simple_ira": { service_family: "portfolio", transaction_type: "financial_client_onboarding" },
  "financial:401k": { service_family: "portfolio", transaction_type: "financial_client_onboarding" },
  // The purpose-unknown mortgage generic. Traditional board: a record
  // that does not say what it was cannot be assumed to be Private
  // Money, and Mike's ledger is the wrong place to guess into.
  "mortgage:mortgage_loan": { service_family: "loan", transaction_type: "mortgage_loan_progression" },
  // Planning — THREE boards by product shape (case types differ, board
  // follows the shape): recurring health/Medicare coverage stays on
  // `life_health_case`; medically-underwritten life/disability/annuity
  // split onto `planning_life_case` (2026-08-29 — see the matching
  // comment in OPPORTUNITY_SERVICE_DEFAULTS above for the full why);
  // the estate plan keeps its own signing-ceremony board. This
  // previously pointed every case type here at `estate_plan_case`,
  // which was the exact bug Grant named on 2026-08-01 (won life/
  // Medicare/disability cases showing up on the Wills & Trusts board),
  // left unfixed in this one direct-create lookup even after the
  // Opportunity-won path was corrected.
  ...Object.fromEntries(
    [
      "health_case",
      "medicare_case",
      "medishare_case",
      "dental_case",
      "vision_case",
      "short_term_medical_case",
    ].map((t) => [
      `planning:${t}`,
      { service_family: "case", transaction_type: "life_health_case" },
    ]),
  ),
  ...Object.fromEntries(
    ["life_case", "disability_case", "long_term_care_case", "annuity_case"].map((t) => [
      `planning:${t}`,
      { service_family: "case", transaction_type: "planning_life_case" },
    ]),
  ),
  "planning:estate_plan": { service_family: "case", transaction_type: "estate_plan_case" },
  // Realty — side IS the family; one board either way.
  "realty:listing": { service_family: "listing", transaction_type: "realty_transaction" },
  "realty:home_purchase": { service_family: "home_purchase", transaction_type: "realty_transaction" },
};

/**
 * Look up (service_family, transaction_type) for a directly-created
 * Processing record, given its company + chosen service_type. Returns
 * null for an unrecognized (company, service_type) pair — the caller
 * should treat that as "can't derive," the same posture
 * isDerivedByFallback names for the opportunity-conversion path. Never
 * guesses a board.
 */
export function deriveServiceShapeForType(
  companySlug: string,
  serviceType: string,
): ServiceShapeForType | null {
  return SERVICE_SHAPE_BY_TYPE[`${companySlug}:${serviceType}`] ?? null;
}

// ─── Backfilling a product record for an orphan Processing record ──
//
// Investigation, 2026-08-10 (Brian's "Mark in management" error on the
// DeWitt household): 107 of 129 open Processing records in production
// (65 Private Money loans, 21 Planning estate plans, 15 Traditional
// mortgage loans, 6 Financial onboardings) have NO linked_service_record
// — they were written by data importers (101 carry an ac_deal_id, the
// other 6 came from the one-shot Financial deals import) that created
// the Processing record but never created the product record that goes
// with it. transition-to-completed refused to close any of them,
// because completing a delivery with nothing to deliver would leave the
// household showing no product after "completion" — but it gave the
// advisor no way forward. The two in-app creation paths (New Processing
// record, Mark Won) both already create the product record correctly,
// so this is a closed, fixed-size population, not an ongoing leak.
//
// This is the REVERSE of OPPORTUNITY_SERVICE_DEFAULTS above: given a
// Processing record's own (company, transaction_type) — not an
// opportunity_type — what product should it be understood to deliver.
// Deliberately reuses COMPANY_SERVICE_DEFAULTS / GLOBAL_FALLBACK /
// isDerivedByFallback rather than inventing a parallel "company default"
// concept: a company's generic product is the same answer regardless of
// which direction (opportunity won, or orphan Processing record) is
// asking for it.
//
// Only entries whose product can't be read off the company default need
// naming here. Most transaction_type values (`policy_issuance`,
// `mortgage_loan_progression`, `financial_client_onboarding`, …) already
// equal their company's own default transaction_type, so the fallback
// tier resolves them correctly with no entry needed.
const TRANSACTION_SERVICE_DEFAULTS: Record<string, OpportunityServiceDefaults> = {
  // Private Money is Mortgage's non-default board (the company default
  // points at Traditional) and is migrated under two type spellings —
  // see the "same board" comment in company-stage-templates.ts.
  "mortgage:private_money_loan": {
    service_type: "private_money_loan",
    service_family: "loan",
    transaction_type: "private_money_loan",
  },
  "mortgage:private_money_loan_progression": {
    service_type: "private_money_loan",
    service_family: "loan",
    transaction_type: "private_money_loan_progression",
  },
  // The estate-plan drafting board is Planning's non-default board (the
  // company default points at the Life & Health Case board) and is
  // migrated under two type spellings — see company-stage-templates.ts.
  "planning:estate_plan": {
    service_type: "estate_plan",
    service_family: "case",
    transaction_type: "estate_plan",
  },
  "planning:estate_plan_case": {
    service_type: "estate_plan",
    service_family: "case",
    transaction_type: "estate_plan_case",
  },
  // The Life board (split out 2026-08-29, alongside `life_health_case`
  // below). Without this entry, backfilling a Life record's missing SR
  // at completion would fall through to COMPANY_SERVICE_DEFAULTS.planning
  // — the HEALTH-shaped company default — which is exactly the
  // wrong-board bug this whole split exists to end. `life_case` is the
  // representative product (an advisor can retype the specific service
  // record after); what matters here is landing the SR on the Life
  // board's product family, not Health's.
  "planning:planning_life_case": {
    service_type: "life_case",
    service_family: "case",
    transaction_type: "planning_life_case",
  },
  // The Health & Medicare board's OWN type, spelled out for the same
  // reason: without it, a Health record's completion-time backfill
  // happens to land correctly today only because COMPANY_SERVICE_
  // DEFAULTS.planning ALSO points at life_health_case — a coincidence,
  // not a guarantee, and one this split's sibling entry above no longer
  // shares. Making it explicit here means neither board's correctness
  // depends on the other's default staying aligned by chance.
  "planning:life_health_case": {
    service_type: "health_case",
    service_family: "case",
    transaction_type: "life_health_case",
  },
};

/**
 * The product (service_type, service_family) an existing Processing
 * record should be understood to deliver, given its own company +
 * transaction_type — used to backfill a missing service record on
 * completion rather than refuse the close outright.
 *
 * Resolution order mirrors deriveOpportunityServiceDefaults:
 *   1. `${companySlug}:${transactionType}` — the exact product.
 *   2. The company's default product.
 *   3. The global fallback (unknown company only — check
 *      isDerivedByFallback(companySlug) and refuse rather than write it).
 */
export function deriveServiceDefaultsForTransactionType(
  transactionType: string,
  companySlug?: string | null,
): OpportunityServiceDefaults {
  const company = (companySlug ?? "").trim();
  if (company) {
    const exact = TRANSACTION_SERVICE_DEFAULTS[`${company}:${transactionType}`];
    if (exact) return exact;
    const byCompany = COMPANY_SERVICE_DEFAULTS[company];
    if (byCompany) return byCompany;
  }
  return GLOBAL_FALLBACK;
}
